import { ReactNode } from 'react'

import { ModelSettings, CMSList } from '@tribeplatform/react-components/CMS'
import { I18nShape, T } from '@tribeplatform/react-components/i18n'
import { TemplateSidebar } from '@tribeplatform/react-components/Templates'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'

import { AppearanceColors } from '../Editor/Appearance/AppearanceColors.js'
import { AppearanceLogos } from '../Editor/Appearance/AppearanceLogos.js'
import { AppearanceStyles } from '../Editor/Appearance/AppearanceStyles.js'
import { AppearanceTypography } from '../Editor/Appearance/AppearanceTypography.js'
import { CreateSpaceTemplateSidebar } from '../Editor/Sidebar/CreateSpaceTemplateSidebar.js'
import {
  NavigationSidebarEditor,
  SpaceSidebarEditor,
  ManageSpacesListSidebar,
  ManagePostTypesSidebar,
  PostTypeSidebarEditor,
} from '../Editor/Sidebar/index.js'
import { StoreSidebar } from '../Store/StoreSidebar.js'

import { AppearanceColorsSidebarTitle } from './AppearanceColorsSidebarTitle.js'
import { PostTypeBlocksSidebarTitle } from './PostTypeBlocksSidebarTitle.js'
import { SpaceBlocksSidebarTitle } from './SpaceBlocksSidebarTitle.js'

export interface SidebarItem {
  title: ReactNode
  description?: ReactNode
  icon?: SvgIconName | JSX.Element
  path?: string
  href?: string
  exact?: boolean
  children?: Array<SidebarItem>
  component?: JSX.Element
  backTo?: string | false
  featureFlag?: string
  arrow?: boolean
  key?: string
  noPaddingX?: boolean
  noPaddingY?: boolean
  noNest?: boolean
  hidden?: boolean
  fullPage?: boolean
}

export const sidebarsFactory: (
  intl: I18nShape,
  eventContentModuleAccess: boolean,
) => Array<SidebarItem> = (intl, eventContentModuleAccess) => {
  const { $t } = intl
  return [
    {
      title: $t({ defaultMessage: 'People', id: 'Generics.People' }),
      icon: 'members',
      path: '/manage/people/members',
      children: [
        {
          title: $t({ defaultMessage: '{MEMBERS_CC}', id: 'Generics.Members' }),
          path: '/manage/people/members',
        },
        {
          title: $t({
            defaultMessage: 'Staff',
            id: 'Generics.Staff',
          }),
          path: '/manage/people/staff',
        },
        {
          title: $t({
            defaultMessage: 'Invitations',
            id: 'Generics.Invitations',
          }),
          path: '/manage/people/invitations',
        },
        {
          title: $t({
            defaultMessage: 'Profile fields',
            id: 'Generics.ProfileFields',
          }),
          path: '/manage/people/fields',
        },
        {
          title: $t({ defaultMessage: 'Badges', id: 'Generics.Badges' }),
          path: '/manage/people/badges',
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Content', id: 'Generics.Content' }),
      icon: 'content',
      path: '/manage/content',
      children: [
        {
          title: $t({ defaultMessage: 'Posts', id: 'Generics.Posts' }),
          path: '/manage/content',
          exact: true,
        },
        ...(eventContentModuleAccess
          ? [
              {
                title: $t({ defaultMessage: 'Events', id: 'Generics.Events' }),
                path: '/manage/content/events',
                exact: true,
              },
            ]
          : []),
        {
          title: $t({ defaultMessage: '{SPACES_CC}', id: 'Generics.Spaces' }),
          path: '/manage/content/spaces',
          exact: true,
        },
        {
          title: $t({ defaultMessage: '{TAGS_CC}', id: 'Generics.Tags' }),
          path: '/manage/content/tags',
        },
        {
          title: (
            <span>
              <T defaultMessage="CMS" id="Generics.CMS" />{' '}
              <Badge size="sm" variant="neutral">
                <T defaultMessage="Beta" id="Generics.Beta" />
              </Badge>
            </span>
          ),
          path: '/manage/content/cms',
          exact: true,
          arrow: true,
          noPaddingX: true,
          component: <CMSList />,
          backTo: '/manage/content',
        },
        {
          title: $t({
            defaultMessage: 'CMS',
            id: 'Generics.CMS',
          }),
          path: '/manage/content/cms/:slug',
          arrow: true,
          noPaddingX: true,
          component: <ModelSettings />,
          backTo: false,
          hidden: true,
          fullPage: true,
        },
      ],
    },
    {
      title: $t({
        defaultMessage: 'Design studio',
        id: 'Generics.DesignStudio',
      }),
      icon: 'customizer',
      path: '/manage/customizer',
      backTo: false,
      noPaddingX: true,
      description: $t({
        defaultMessage:
          'Structure and configure the overall look and feel of your community.',
        id: 'Admin.Sidebar.CustomizerDescription',
      }),
      children: [
        {
          title: $t({
            defaultMessage: '{COLLECTIONS_CC} and {SPACES}',
            id: 'Generics.CollectionsAndSpaces',
          }),
          path: '/manage/customizer/spaces',
          backTo: '/manage/customizer',
          component: <ManageSpacesListSidebar />,
          arrow: true,
          noPaddingX: true,
          children: [
            {
              title: $t({ defaultMessage: '{SPACE_CC}', id: 'Generics.Space' }),
              path: '/manage/customizer/spaces/:id',
              backTo: '/manage/customizer/spaces',
              children: [
                {
                  title: <SpaceBlocksSidebarTitle />,
                  backTo: '/manage/customizer/spaces',
                  path: '/manage/customizer/spaces/:id/blocks',
                  component: <SpaceSidebarEditor />,
                  noPaddingX: true,
                },
              ],
            },
          ],
        },
        {
          title: $t({
            defaultMessage: 'Header and sidebar',
            id: 'Generics.HeaderAndSidebar',
          }),
          description: $t({
            defaultMessage:
              'Adjust layout settings for the Header and Sidebar, controlling their visibility and arrangement site-wide.',
            id: 'AdminPanel.Layout.Description',
          }),
          path: '/manage/customizer/navigation',
          component: <NavigationSidebarEditor />,
          arrow: true,
          noPaddingX: true,
        },
        {
          title: (
            <span>
              <T defaultMessage="CMS pages" id="Generics.CMSPages" />{' '}
              <Badge size="sm" variant="neutral">
                <T defaultMessage="Beta" id="Generics.Beta" />
              </Badge>
            </span>
          ),
          path: '/manage/customizer/cms',
          backTo: '/manage/customizer',
          component: <ManagePostTypesSidebar />,
          arrow: true,
          children: [
            {
              title: $t({
                defaultMessage: 'CMS page',
                id: 'Generics.CMSPage',
              }),
              path: '/manage/customizer/cms/:postTypeId',
              backTo: '/manage/customizer/cms',
              children: [
                {
                  title: <PostTypeBlocksSidebarTitle />,
                  backTo: '/manage/customizer/cms',
                  path: '/manage/customizer/cms/:postTypeId/blocks',
                  component: <PostTypeSidebarEditor />,
                  noPaddingX: true,
                },
              ],
            },
          ],
        },
        {
          title: $t({
            defaultMessage: 'Create {SPACE}',
            id: 'Generics.CreateSpace',
          }),
          path: '/manage/customizer/create-space',
          component: <TemplateSidebar />,
          backTo: '/manage/customizer/spaces',
          hidden: true,
        },
        {
          title: (
            <span>
              <T defaultMessage="Templates" id="Generics.Templates" />{' '}
              <Badge size="sm" variant="neutral">
                <T defaultMessage="Internal only" id="Generics.InternalOnly" />
              </Badge>
            </span>
          ),
          path: '/manage/customizer/templates',
          featureFlag: 'create_space_template',
          noPaddingX: true,
          noNest: true,
          children: [
            {
              title: $t({
                defaultMessage: 'Create template',
                id: 'Generics.CreateTemplate',
              }),
              path: '/manage/customizer/templates/new',
              component: <CreateSpaceTemplateSidebar />,
              backTo: '/manage/customizer/templates',
            },
            {
              title: $t({
                defaultMessage: 'Edit template',
                id: 'Generics.EditTemplate',
              }),
              path: '/manage/customizer/templates/edit',
              component: <CreateSpaceTemplateSidebar />,
              backTo: '/manage/customizer/templates',
            },
          ],
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Appearance', id: 'Generics.Appearance' }),
      icon: 'appearance',
      path: '/manage/appearance',
      backTo: false,
      noPaddingX: true,
      children: [
        {
          title: $t({ defaultMessage: 'Logos', id: 'Generics.Logos' }),
          path: '/manage/appearance/logos',
          backTo: '/manage/appearance',
          component: <AppearanceLogos />,
          arrow: true,
        },
        {
          title: <AppearanceColorsSidebarTitle />,
          path: '/manage/appearance/colors',
          backTo: false,
          component: <AppearanceColors />,
          arrow: true,
          noPaddingX: true,
          noPaddingY: true,
        },
        {
          title: $t({
            defaultMessage: 'Typographies',
            id: 'Generics.Typographies',
          }),
          description: $t({
            defaultMessage:
              'Pick a font pack. Your global styles appear across your community.',
            id: 'Admin.Sidebar.TypographyDescription',
          }),
          path: '/manage/appearance/typography',
          backTo: '/manage/appearance',
          component: <AppearanceTypography />,
          arrow: true,
        },
        {
          title: $t({
            defaultMessage: 'Styles',
            id: 'Generics.Styles',
          }),
          path: '/manage/appearance/styles',
          backTo: '/manage/appearance',
          component: <AppearanceStyles />,
          arrow: true,
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Settings', id: 'Generics.Settings' }),
      icon: 'settings',
      path: '/manage/settings/network',
      children: [
        {
          title: $t({
            defaultMessage: 'Site settings',
            id: 'Generics.CommunitySettings',
          }),
          path: '/manage/settings/network',
        },

        {
          title: $t({
            defaultMessage: 'Authentication',
            id: 'Generics.Authentication',
          }),
          path: '/manage/settings/authentication',
        },
        {
          title: $t({ defaultMessage: 'Domain', id: 'Generics.Domain' }),
          path: '/manage/settings/domain',
        },
        {
          title: $t({
            defaultMessage: 'Search',
            id: 'Generics.Search',
          }),
          path: '/manage/settings/search',
        },
        {
          title: $t({
            defaultMessage: 'Messaging',
            id: 'Generics.Messaging',
          }),
          path: '/manage/settings/messaging',
        },
        {
          title: $t({
            defaultMessage: 'Moderation',
            id: 'Generics.Moderation',
          }),
          path: '/manage/settings/moderation',
        },
        {
          title: $t({
            defaultMessage: 'Localization',
            id: 'Generics.Localization',
          }),
          path: '/manage/settings/localization',
        },
        {
          title: $t({
            defaultMessage: 'Notifications',
            id: 'Generics.Notifications',
          }),
          path: '/manage/settings/notifications',
        },
        {
          title: $t({
            defaultMessage: 'SEO settings',
            id: 'Generics.SeoSettings',
          }),
          path: '/manage/settings/seo',
        },
        {
          title: $t({
            defaultMessage: 'Security & Privacy',
            id: 'Generics.SecurityAndPrivacy',
          }),
          path: '/manage/settings/security',
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Billing', id: 'Generics.Billing' }),
      path: '/manage/billing',
      icon: 'credit-card',
      backTo: false,
      children: [
        {
          title: $t({
            defaultMessage: 'Summary',
            id: 'Generics.Summary',
          }),
          path: '/manage/billing',
          exact: true,
        },
        {
          title: $t({
            defaultMessage: 'Subscription plans',
            id: 'Generics.SubscriptionPlans',
          }),
          path: '/manage/billing/plans',
        },
        {
          title: $t({
            defaultMessage: 'Service usage',
            id: 'Generics.ServiceUsage',
          }),
          path: '/manage/billing/usages',
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'Reports', id: 'Generics.Reports' }),
      icon: 'analytics',
      path: '/manage/reports/analytics',
      key: '/manage/reports/analytics#root',
      backTo: false,
      children: [
        {
          title: $t({ defaultMessage: 'Overview', id: 'Generics.Overview' }),
          key: '/manage/reports/analytics',
          path: '/manage/reports/analytics',
          arrow: false,
        },
        {
          title: $t({
            defaultMessage: 'Reach & engagement',
            id: 'Generics.ReachAndEngagement',
          }),
          path: '/manage/reports/analytics/reach',
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'People', id: 'Generics.People' }),
          path: '/manage/reports/analytics/people',
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'Posts', id: 'Generics.Posts' }),
          path: '/manage/reports/analytics/posts',
          arrow: false,
        },
        ...(eventContentModuleAccess
          ? [
              {
                title: $t({ defaultMessage: 'Events', id: 'Generics.Events' }),
                path: '/manage/reports/analytics/events',
                arrow: false,
              },
            ]
          : []),
        {
          title: $t({ defaultMessage: '{SPACES_CC}', id: 'Generics.Spaces' }),
          path: '/manage/reports/analytics/spaces',
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'Messages', id: 'Generics.Messages' }),
          path: '/manage/reports/analytics/messages',
          arrow: false,
        },
        {
          title: $t({ defaultMessage: 'Audit logs', id: 'Generics.AuditLogs' }),
          path: '/manage/reports/audit-logs',
          key: '/manage/reports/audit-logs#AuditLogs',
        },
        {
          title: $t({ defaultMessage: 'Email logs', id: 'Generics.EmailLogs' }),
          path: '/manage/reports/email-logs',
        },
      ],
    },
    {
      title: $t({ defaultMessage: 'App store', id: 'Generics.AppStore' }),
      icon: 'grid',
      path: '/manage/app-store',
      href: '/manage/app-store/integrations',
      component: <StoreSidebar />,
    },
    {
      title: $t({ defaultMessage: 'Onboarding', id: 'Generics.Onboarding' }),
      icon: 'graduation-hat-01',
      path: '/manage/onboarding',
    },
  ]
}

export const virtualSidebarRootFactory: (
  intl: I18nShape,
  eventContentModuleAccess: boolean,
) => SidebarItem = (intl, eventContentModuleAccess) => ({
  title: '/',
  icon: undefined,
  path: '/manage',
  children: sidebarsFactory(intl, eventContentModuleAccess),
})
